import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter as FilterIcon } from 'lucide-react';
import FilterDrawer from '../../../shared/FilterDrawer';
import DateInput from '../../../shared/DateInput';
import ParagraphText from '../../../shared/ParagraphText';
import { Pagination, PAGE_SIZE_OPTIONS, FALLBACK_PAGE_SIZE, useDefaultPageSize, getCachedDefaultPageSize } from '../../../shared/pagination';
import { formatDateTime } from '../../../shared/tenantClock';
import { useErrorToast } from '../../../shared/ErrorToastContext';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import CancelButton from '../../../shared/CancelButton';

// Platform > Audit Log (issue #391): who did what and when, newest first —
// portal sign-ins, successful or not, and every save that changed a Quote
// or Sales Order, with each changed field's before and after values. Read
// only: nothing here changes or removes an entry. A row click shows its
// changes (Inline Row Expand). A Quote or Sales Order's "Audit Log" action
// opens this screen filtered to that document
// (?document_type=&document_id=&number=).

const TH = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const TD = { padding: '6px 8px', fontSize: 14, verticalAlign: 'top' };
const CLAMP = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const FIELD_LABEL_STYLE = { fontSize: 12, color: '#5a6b85' };
const FIELD_INPUT_STYLE = { padding: '6px 10px', borderRadius: 6, border: '1px solid #bfc8d6', fontSize: 14, width: '100%' };
const MUTED = { color: '#8a94a6' };

const EVENT_LABELS = {
  portal_sign_in: 'Portal sign-in',
  portal_sign_in_failed: 'Portal sign-in failed',
  document_edited: 'Document edited',
};
const FAILURE_LABELS = {
  unknown_login: 'No login with that email',
  wrong_password: 'Wrong password',
  invite_not_finished: 'Invite not finished',
  switched_off: 'Login switched off',
  customer_paused: 'Customer is paused',
  paused: 'Login paused after too many wrong passwords',
};
const DOCUMENT_TYPE_LABELS = { quote: 'Quote', sales_order: 'Sales Order' };

const DEFAULT_FILTERS = { from: '', to: '', event_type: 'all', actor: 'all', customer_id: 'all' };

// Who did it: the staff member, the Customer Login, or both when an Admin
// was switched in to the portal with the Login Switcher.
function whoLabel(row) {
  if (row.login_email && row.user_name) return `${row.login_email} (switched in: ${row.user_name})`;
  return row.login_email || row.user_name || '—';
}

function plural(n, word) {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

// The one-line summary in the Details column.
function summary(row) {
  if (row.event_type === 'portal_sign_in_failed') return FAILURE_LABELS[row.detail?.reason] || 'Failed';
  if (row.event_type === 'portal_sign_in') return row.ip_address ? `From ${row.ip_address}` : '';
  if (row.event_type === 'document_edited') {
    const fields = row.detail?.fields?.length || 0;
    const lines = row.detail?.lines?.length || 0;
    return [fields && `${plural(fields, 'field')} changed`, lines && `${plural(lines, 'line')} changed`].filter(Boolean).join(', ');
  }
  return '';
}

function Value({ text }) {
  return text ? <ParagraphText text={text} /> : <span style={MUTED}>(blank)</span>;
}

function valuesText(values) {
  return Object.entries(values || {}).filter(([, v]) => v).map(([k, v]) => `${k}: ${v}`).join(' · ');
}

const LINE_CHANGE_LABELS = { added: 'Line added', removed: 'Line removed', changed: 'Line changed' };

function EntryDetail({ row }) {
  const cell = { padding: '4px 10px 4px 0', fontSize: 13, verticalAlign: 'top' };
  return (
    <div style={{ fontSize: 13, color: '#334155' }}>
      <div style={{ color: '#5a6b85', marginBottom: 8 }}>
        {[row.ip_address && `IP address ${row.ip_address}`, row.login_email && row.event_type === 'portal_sign_in_failed' && `Email typed: ${row.login_email}`].filter(Boolean).join(' · ')}
      </div>
      {row.detail?.fields?.length > 0 && (
        <table style={{ borderCollapse: 'collapse', marginBottom: 10, width: '100%', tableLayout: 'fixed' }}>
          <colgroup><col style={{ width: 200 }} /><col /><col /></colgroup>
          <thead>
            <tr style={{ textAlign: 'left', color: '#5a6b85' }}>
              <th style={cell}>Field</th><th style={cell}>Before</th><th style={cell}>After</th>
            </tr>
          </thead>
          <tbody>
            {row.detail.fields.map((f) => (
              <tr key={f.field}>
                <td style={{ ...cell, fontWeight: 600 }}>{f.field}</td>
                <td style={cell}><Value text={f.before} /></td>
                <td style={cell}><Value text={f.after} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(row.detail?.lines || []).map((l, idx) => (
        <div key={idx} style={{ padding: '6px 0', borderTop: '1px solid #e3e7ee' }}>
          <div><span style={{ fontWeight: 600 }}>{LINE_CHANGE_LABELS[l.change]}:</span> {l.line}</div>
          {l.change === 'changed' ? (
            l.changes.map((c) => (
              <div key={c.field} style={{ paddingLeft: 12 }}>
                {c.field}: {c.before || <span style={MUTED}>(blank)</span>} → {c.after || <span style={MUTED}>(blank)</span>}
              </div>
            ))
          ) : (
            <div style={{ paddingLeft: 12, color: '#475569' }}>{valuesText(l.values)}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function AuditLogPage() {
  const showError = useErrorToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedDocument = searchParams.get('document_type') && searchParams.get('document_id')
    ? { type: searchParams.get('document_type'), id: searchParams.get('document_id'), number: searchParams.get('number') }
    : null;
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterDraft, setFilterDraft] = useState(null);
  const [options, setOptions] = useState({ actors: [], customers: [] });
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  const [pagination, setPagination] = useState({ page: 1, limit, total: 0, pages: 1 });
  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter((k) => String(filters[k] || '') !== String(DEFAULT_FILTERS[k] || '')).length;

  useDefaultPageSize(setLimit);

  useEffect(() => {
    axios.get('/api/audit-log/filter-options')
      .then((res) => setOptions(res.data.data || { actors: [], customers: [] }))
      .catch(() => setOptions({ actors: [], customers: [] }));
  }, []);

  const documentType = linkedDocument?.type;
  const documentId = linkedDocument?.id;
  useEffect(() => {
    setLoading(true);
    const pick = (value) => (value && value !== 'all' ? value : undefined);
    axios.get('/api/audit-log', {
      params: {
        from: pick(filters.from),
        to: pick(filters.to),
        event_type: pick(filters.event_type),
        actor: pick(filters.actor),
        customer_id: pick(filters.customer_id),
        document_type: documentType || undefined,
        document_id: documentId || undefined,
        document: documentId ? undefined : (search.trim() || undefined),
        page,
        limit,
      },
    })
      .then((res) => {
        setRows(res.data.data || []);
        setPagination(res.data.pagination || { page, limit, total: 0, pages: 1 });
      })
      .catch((err) => {
        setRows([]);
        setPagination({ page, limit, total: 0, pages: 1 });
        showError(err, "Couldn't load the Audit Log");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [filters, search, documentType, documentId, page, limit]);

  const applyFilters = (values) => { setFilters(values); setPage(1); };
  const openFilterPanel = () => setFilterDraft({ ...filters });
  const closeFilterPanel = () => setFilterDraft(null);
  const setFilterDraftField = (key, value) => setFilterDraft((prev) => ({ ...prev, [key]: value }));
  const applyFilterPanel = () => { applyFilters(filterDraft); closeFilterPanel(); };
  const clearFilterPanel = () => { applyFilters(DEFAULT_FILTERS); closeFilterPanel(); };
  const clearLinkedDocument = () => { setSearchParams({}); setPage(1); };

  const staffActors = options.actors.filter((a) => a.kind === 'staff');
  const loginActors = options.actors.filter((a) => a.kind === 'login');

  const renderDrawerFields = (draft, setDraftField) => (
    <>
      <div>
        <label style={FIELD_LABEL_STYLE}>From</label><br />
        <DateInput className="styled-input" value={draft.from} onChange={(e) => setDraftField('from', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>To</label><br />
        <DateInput className="styled-input" value={draft.to} onChange={(e) => setDraftField('to', e.target.value)} style={FIELD_INPUT_STYLE} />
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Event</label><br />
        <select className="styled-input" value={draft.event_type} onChange={(e) => setDraftField('event_type', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All events</option>
          {Object.entries(EVENT_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>User</label><br />
        <select className="styled-input" value={draft.actor} onChange={(e) => setDraftField('actor', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">Everyone</option>
          {staffActors.length > 0 && (
            <optgroup label="Staff">
              {staffActors.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </optgroup>
          )}
          {loginActors.length > 0 && (
            <optgroup label="Customer Logins">
              {loginActors.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </optgroup>
          )}
        </select>
      </div>
      <div>
        <label style={FIELD_LABEL_STYLE}>Customer</label><br />
        <select className="styled-input" value={draft.customer_id} onChange={(e) => setDraftField('customer_id', e.target.value)} style={FIELD_INPUT_STYLE}>
          <option value="all">All Customers</option>
          {options.customers.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
    </>
  );

  const colCount = 6;

  return (
    <div style={{ margin: '32px 24px 48px', background: '#fafafa', border: '1px solid #eceef1', borderRadius: 12, boxShadow: '0 1px 6px rgba(0,0,0,0.04)', paddingBottom: 32 }}>
      <div style={{ padding: '20px 24px 0' }}>
        <h2 style={{ margin: 0, fontWeight: 600, fontSize: 20 }}>Audit Log</h2>
      </div>

      <div style={{ display: 'flex', gap: 10, margin: '16px 24px 0', alignItems: 'center', flexWrap: 'wrap' }}>
        {linkedDocument ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 6px 4px 10px', borderRadius: 999, background: '#eef1f5', border: '1px solid #bfc8d6', fontSize: 13 }}>
            {DOCUMENT_TYPE_LABELS[linkedDocument.type] || 'Document'} {linkedDocument.number || `#${linkedDocument.id}`}
            <button type="button" onClick={clearLinkedDocument} title="Show every document" style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, padding: '0 4px', color: '#5a6b85' }}>×</button>
          </span>
        ) : (
          <div>
            <input
              className="styled-input"
              placeholder="Search document #..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              style={{ width: 240 }}
            />
            {search && (
              <button type="button" onClick={() => { setSearch(''); setPage(1); }} title="Clear search" style={{ marginLeft: 6, padding: '4px 8px', fontSize: 12 }}>×</button>
            )}
          </div>
        )}
        <button
          type="button"
          onClick={openFilterPanel}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 6,
            border: '1px solid #bfc8d6', background: activeFilterCount ? '#eef1f5' : '#fff', fontSize: 14, fontWeight: 500, cursor: 'pointer',
          }}
        >
          <FilterIcon size={15} strokeWidth={2} />
          Filters
          {activeFilterCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 4px', borderRadius: 999, background: '#1976d2', color: '#fff', fontSize: 11, fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {filterDraft && (
        <FilterDrawer
          open
          onOpenChange={(next) => { if (!next) closeFilterPanel(); }}
          title="Filters"
          description="Narrow the Audit Log."
          footer={(
            <>
              <div>
                <button type="button" onClick={clearFilterPanel} style={{ padding: '8px 12px', border: '1px solid #bfc8d6', color: '#5a6b85', background: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                  Clear filters
                </button>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <CancelButton onClick={closeFilterPanel} style={{ padding: '8px 14px' }} />
                <button type="button" onClick={applyFilterPanel} style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Apply</button>
              </div>
            </>
          )}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {renderDrawerFields(filterDraft, setFilterDraftField)}
          </div>
        </FilterDrawer>
      )}

      <div style={{ margin: '18px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 160 }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '22%' }} />
            <col style={{ width: '16%' }} />
            <col style={{ width: '12%' }} />
            <col />
          </colgroup>
          <thead className="list-head" style={{ fontSize: 14 }}>
            <tr>
              <th style={TH}>When</th>
              <th style={TH}>Event</th>
              <th style={TH}>Who</th>
              <th style={TH}>Customer</th>
              <th style={TH}>Document</th>
              <th style={TH}>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colCount} style={{ padding: '14px', textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>No matching entries</td></tr>
            ) : rows.map((row) => {
              const isExpanded = expandedId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <tr
                    className="list-row"
                    onClick={() => setExpandedId(isExpanded ? null : row.id)}
                    style={{ cursor: 'pointer' }}
                    title={isExpanded ? 'Click to hide the details' : 'Click to show the details'}
                  >
                    <td style={TD}>{formatDateTime(row.occurred_at)}</td>
                    <td style={{ ...TD, ...CLAMP, color: row.event_type === 'portal_sign_in_failed' ? '#b91c1c' : undefined }}>{EVENT_LABELS[row.event_type] || row.event_type}</td>
                    <td style={{ ...TD, ...CLAMP }} title={whoLabel(row)}>{whoLabel(row)}</td>
                    <td style={{ ...TD, ...CLAMP }}>{row.customer_name || '—'}</td>
                    <td style={{ ...TD, ...CLAMP }} onClick={(e) => e.stopPropagation()}>
                      {row.link ? <Link to={row.link}>{row.document_number || DOCUMENT_TYPE_LABELS[row.document_type]}</Link> : (row.document_number || '—')}
                    </td>
                    <td style={{ ...TD, ...CLAMP, color: '#475569' }}>{summary(row)}</td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={colCount} style={{ padding: '10px 16px 14px', background: '#f8fafc', borderBottom: '1px solid #e3e7ee' }}>
                        <EntryDetail row={row} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Pagination
        pagination={pagination}
        setPage={setPage}
        totalLabel="Entries"
        rightContent={(
          <select className="styled-input" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }} style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}>
            {PAGE_SIZE_OPTIONS.map((sz) => <option key={sz} value={sz}>{sz} per page</option>)}
          </select>
        )}
      />
    </div>
  );
}

export default AuditLogPage;
